/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { AutocompleteRenderGroupParams } from '@mui/material';
import {
  Autocomplete,
  Box,
  TextField,
  useTheme,
  ListSubheader,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { CustomIconButton, useDebounce } from '@opengeoweb/shared';
import { Close, Airplane, Region, Pinned } from '@opengeoweb/theme';
import { MyMapLocation } from './MyMapLocation';
import type {
  LocationDetail,
  LocationListResult,
  PredefinedOption,
} from './types';
import { useCoreTranslation } from '../../utils/i18n';
import {
  useLocationDetail,
  useLocationList,
} from '../../utils/location-api/hooks';

export interface LocationSearchProps {
  isOpen: boolean;
  mapId?: string;
  onSelectLocation?: (location?: LocationDetail) => void;
  onUpdateMyLocation?: (location?: LocationDetail) => void;
  isMyLocationSelected?: boolean;
}

export const LocationSearch: React.FC<LocationSearchProps> = ({
  isOpen,
  mapId = '',
  onSelectLocation = (): void => {},
  onUpdateMyLocation = (): void => {},
  isMyLocationSelected = false,
}: LocationSearchProps) => {
  const theme = useTheme();
  const { t } = useCoreTranslation();
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<
    LocationListResult | LocationDetail | null
  >(null);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(true);

  const debouncedInputValue = useDebounce(inputValue, 300);
  const { data: locationListData } = useLocationList(debouncedInputValue);

  // For when we are still typing something
  const [pendingValue, setPendingValue] = useState<
    LocationListResult | LocationDetail | null
  >(null);

  // Extract predefined locations from BE (and group them)
  const backendPredefined = useMemo(
    () =>
      (locationListData || []).filter((item) => item.source === 'predefined'),
    [locationListData],
  );

  const groupedPredefinedAreas = useMemo(
    () => [
      {
        header: t('search-airports'),
        icon: Airplane,
        items: backendPredefined.filter((i) => i.group === 'airport'),
      },
      {
        header: t('search-countries-regions-cities'),
        icon: Region,
        items: backendPredefined.filter(
          (i) =>
            i.group === 'country' || i.group === 'city' || i.group === 'region',
        ),
      },
      {
        header: t('search-places'),
        icon: Pinned,
        items: backendPredefined.filter((i) => i.group === 'special'),
      },
    ],
    [backendPredefined, t],
  );

  const predefinedOptions: PredefinedOption[] = useMemo(
    () =>
      groupedPredefinedAreas.flatMap((group) =>
        group.items.map((item) => ({
          ...item,
          groupHeader: group.header,
          groupIcon: group.icon,
        })),
      ),
    [groupedPredefinedAreas],
  );

  // Filter predefined options by search string
  const filteredPredefinedOptions = useMemo(
    () =>
      debouncedInputValue.trim() === ''
        ? predefinedOptions
        : predefinedOptions.filter((option) =>
            option.name
              .toLowerCase()
              .includes(debouncedInputValue.trim().toLowerCase()),
          ),
    [debouncedInputValue, predefinedOptions],
  );

  // Filter out predefined options from locationListData to avoid duplicates
  const locationListResults = useMemo(
    () =>
      (locationListData || []).filter(
        (result) =>
          !filteredPredefinedOptions.some(
            (predef) =>
              predef.id === result.id && predef.source === result.source,
          ),
      ),
    [locationListData, filteredPredefinedOptions],
  );

  const autocompleteOptions: (LocationListResult | PredefinedOption)[] =
    useMemo(
      () =>
        debouncedInputValue.trim() === ''
          ? predefinedOptions
          : [...filteredPredefinedOptions, ...locationListResults],
      [
        debouncedInputValue,
        predefinedOptions,
        filteredPredefinedOptions,
        locationListResults,
      ],
    );

  const { data: locationDetail, isFetching: isFetchingDetail } =
    useLocationDetail(selectedValue);

  useEffect(() => {
    // Only open dropdown if no location is selected
    if (isOpen && !selectedValue) {
      setDropdownOpen(true);
    }
  }, [isOpen, selectedValue]);

  useEffect(() => {
    // Update pin only on confirmed selection
    if (!selectedValue || isFetchingDetail || !isOpen) {
      return;
    }
    onSelectLocation(locationDetail);
  }, [
    selectedValue,
    locationDetail,
    isFetchingDetail,
    isOpen,
    onSelectLocation,
  ]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (isOpen && event.key === 'Escape') {
        if (dropdownOpen) {
          setDropdownOpen(false);
        }
        event.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dropdownOpen, isOpen]);

  return isOpen && mapId ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        position: 'absolute',
        pointerEvents: 'all',
        zIndex: 99,
        left: 50,
        top: 8,
        backgroundColor: theme.palette.geowebColors.textInputField.default.fill,
        height: 40,
        boxShadow: theme.shadows[6],
        width: 'calc(100% - 50px)',
        maxWidth: 380,
      }}
    >
      <MyMapLocation
        isSelected={isMyLocationSelected}
        mapId={mapId}
        onUpdateMyLocation={onUpdateMyLocation}
      />
      <Autocomplete
        autoHighlight
        disablePortal
        filterOptions={(x) => x}
        getOptionKey={(option) => option.id + option.name}
        getOptionLabel={(option) => option.name}
        onClose={() => setDropdownOpen(false)}
        onOpen={() => setDropdownOpen(true)}
        open={dropdownOpen}
        options={autocompleteOptions}
        selectOnFocus
        sx={{
          width: '100%',
          '&.MuiAutocomplete-root .MuiFilledInput-root': {
            height: 40,
            padding: '0px!important',
            '&:before, &:hover:before': {
              borderColor: 'transparent',
            },
          },
          '&.MuiAutocomplete-root .MuiInputLabel-root': {
            lineHeight: '16px',
            maxWidth: 'calc(100% - 40px)',
            ':not(.MuiInputLabel-shrink)': {
              transform: 'translate(12px, 12px) scale(1)',
            },
          },
          '&.MuiAutocomplete-root .MuiFilledInput-input': {
            lineHeight: '20px',
            paddingLeft: '12px',
            paddingTop: '20px',
          },
        }}
        {...(debouncedInputValue.trim() === ''
          ? {
              groupBy: (option: LocationListResult) =>
                'groupHeader' in option &&
                typeof option.groupHeader === 'string'
                  ? option.groupHeader
                  : '',
              renderGroup: (params: AutocompleteRenderGroupParams) => (
                <React.Fragment key={params.key}>
                  <ListSubheader
                    disableSticky
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 400,
                      fontSize: '16px',
                      py: 1,
                      my: 0,
                      minHeight: '26px',
                      lineHeight: 1,
                    }}
                  >
                    {params.group}
                  </ListSubheader>
                  {Array.isArray(params.children)
                    ? params.children
                    : [params.children]}
                </React.Fragment>
              ),
            }
          : {})}
        /* eslint-disable @typescript-eslint/no-explicit-any */
        clearIcon={null}
        inputValue={inputValue}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={t('search-no-results')}
        onChange={(_event, newValue, reason) => {
          if (newValue && typeof newValue !== 'string') {
            if (reason === 'selectOption') {
              // Confirm if user clicked
              setSelectedValue(newValue);
              setPendingValue(null);
              setInputValue(newValue.name);
              setDropdownOpen(false);
            } else {
              // Just a pending result when typing
              setPendingValue(newValue);
              setInputValue(newValue.name);
            }
          }
        }}
        popupIcon={null}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            fullWidth
            label={t('search-title-long')}
            onChange={(event) => {
              setInputValue(event.target.value);
              if (selectedValue) {
                setSelectedValue(null);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && pendingValue) {
                setSelectedValue(pendingValue); // confirm selection
                setPendingValue(null); // clear pending
                setDropdownOpen(false);
                event.preventDefault();
              }
              if (
                params.inputProps &&
                typeof params.inputProps.onKeyDown === 'function'
              ) {
                params.inputProps.onKeyDown(
                  event as React.KeyboardEvent<HTMLInputElement>,
                );
              }
            }}
            size="medium"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <CustomIconButton
                    onClick={() => {
                      setInputValue('');
                      setSelectedValue(null);
                      setPendingValue(null);
                      onSelectLocation(undefined);
                    }}
                    sx={{ marginRight: 1 }}
                    tooltipTitle={t('search-clear')}
                  >
                    <Close />
                  </CustomIconButton>
                ),
              },
            }}
            variant="filled"
          />
        )}
        renderOption={(
          props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
          option: LocationListResult | PredefinedOption,
        ): React.ReactElement => {
          const { key, ...rest } = props;
          let icon: React.ReactElement | null = null;
          if (typeof (option as any).groupIcon === 'function') {
            const Icon = (option as any).groupIcon;
            const iconElement = (
              <Icon
                data-testid={`${option.name}-svg`}
                fontSize="small"
                sx={{ mr: 1 }}
              />
            );
            icon = React.cloneElement(
              iconElement,
              {},
              React.Children.map(iconElement.props.children, (child) =>
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                React.isValidElement(child)
                  ? React.cloneElement(child as React.ReactElement, {
                      'data-testid': `${option.name}-path`,
                    })
                  : child,
              ),
            );
          }
          return (
            <li key={key} {...rest}>
              {icon}
              {option.name}
            </li>
          );
        }}
        value={selectedValue}
      />
    </Box>
  ) : null;
};
